import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Spinner, Offcanvas } from "react-bootstrap";
import * as apicall from "../../components/Apicall";
import * as inventoryService from "../../services/inventoryService";
import * as estimationService from "../../services/estimationService";
import { describeConflict } from "../../services/resourceService";
import NewAppointment from "../../components/Appointment/NewAppointment";

// ── Payment methods ───────────────────────────────────────────────────────────
// INR → Razorpay handles card/UPI/netbanking/wallet inside its own modal.
// Non-INR (AED etc.) → Stripe handles card payment; UPI not applicable.
const PAYMENT_METHODS_INR = [
  { id: "cash", label: "Cash", emoji: "💵", offline: true },
  // { id: "card",   label: "Card",       sub: "Razorpay · Card / POS", emoji: "💳", offline: false },
  // { id: "upi",    label: "UPI / QR",   sub: "Razorpay · UPI & QR",   emoji: "📱", offline: false },
  { id: "online", label: "Pay Online", emoji: "🔗", offline: false },
  // { id: "split",  label: "Split",      sub: "Razorpay · split pay",  emoji: "⚡", offline: false },
];

const PAYMENT_METHODS_NON_INR = [
  { id: "cash", label: "Cash", emoji: "💵", offline: true },
  // { id: "card",   label: "Card",       sub: "Stripe · Card",       emoji: "💳", offline: false },
  { id: "online", label: "Pay Online", emoji: "🔗", offline: false },
];

// ── Script loaders ────────────────────────────────────────────────────────────


const CHECKOUT_STATE_KEY = "utilstop_checkout_state";

export default function Checkout() {
  const location = useLocation();
  const navigate = useNavigate();

  // Restore from sessionStorage when coming back from Stripe (location.state lost on full-page redirect)
  const _restoredState = location.state || (() => {
    try { return JSON.parse(sessionStorage.getItem(CHECKOUT_STATE_KEY) || "null"); } catch { return null; }
  })();
  const { bookingValues, partners, partner_id } = _restoredState || {};
  // Where Back / "+ Add Item" should return to when this came from
  // Bookings.jsx (a full page, not a sidepanel) — defaults to Bookings for
  // callers that don't pass it, so existing behavior is unchanged. New
  // Appointment bookings (isAppointmentForm) don't use this at all — see
  // showAppointmentPanel below, which reopens the form inline instead.
  const returnPath = _restoredState?.returnPath || "/vendor_bookings";
  const isAppointmentForm = !!bookingValues?.isAppointmentForm;

  // ── Currency helpers ────────────────────────────────────────────────────────
  const currency = bookingValues?.currency || "AED";
  const isINR = currency === "INR";
  const sym = isINR ? "₹" : "AED ";
  const fmt = (n) =>
    Number(n || 0).toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // ── Build initial cart from priceSection ────────────────────────────────────
  const buildCart = (ps) => {
    return (ps || [])
      .filter((i) => {
        const n = (i.name || "").toLowerCase();
        return !n.includes("tax") && !n.includes("discount") && !n.includes("total");
      })
      .map((i) => ({
        ...i,
        qty: 1,
        unitPrice: Number(i.amount || 0),
      }));
  };

  const priceSection = bookingValues?.priceSection || [];
  const taxItem = priceSection.find((i) => (i.name || "").toLowerCase().includes("tax"));
  const taxPercent = taxItem
    ? parseFloat((taxItem.name || "").match(/[\d.]+/)?.[0] || 0)
    : 0;
  const discItem = priceSection.find((i) => (i.name || "").toLowerCase() === "discount");

  const isEditMode = !!(bookingValues?.group_id);
  const PENDING_GID_KEY = "utilstop_checkout_pending_gid";

  // ── All hooks — must be before any early return (Rules of Hooks) ───────────
  const [cartItems, setCartItems] = useState(() => buildCart(priceSection));
  // Discount is set once on the Booking page's price summary and carried
  // through here read-only - no separate editable control on Checkout.
  const discount = Number(discItem?.amount || 0);
  const [tipPercent, setTipPercent] = useState(0);
  const [paidAmount, setPaidAmount] = useState(Number(bookingValues?.paid_amount || 0));
  const [selectedPayment, setSelectedPayment] = useState("cash");
  const [loading, setLoading] = useState(false);
  const [groupId, setGroupId] = useState(() => {
    if (isEditMode) return Number(bookingValues?.group_id);
    const stored = sessionStorage.getItem(PENDING_GID_KEY);
    return stored ? Number(stored) : null;
  });
  // New Appointment reopens inline as a sidepanel on top of Checkout — same
  // pattern Payments.jsx/DashboardDetailed.jsx use for their own "New
  // Appointment" button — instead of navigating away to another page.
  const [showAppointmentPanel, setShowAppointmentPanel] = useState(false);
  // "Pay Online" isn't wired up for vendors yet - clicking it explains that
  // instead of letting them pick it, and resets to Cash on dismiss.
  const [showOnlineDisabledModal, setShowOnlineDisabledModal] = useState(false);

  // Payment Status now lives here instead of on the Booking form - it's
  // only actually known once the vendor picks how they're being paid.
  const [paymentStatusOptions, setPaymentStatusOptions] = useState([]);
  const [cashPaymentStatus, setCashPaymentStatus] = useState(null);

  // ── Fresh navigation from booking form: clear any stale pending group_id ──────
  // location.state is only set on direct navigation (not sessionStorage restore).
  // This prevents a previous Stripe/online booking's group_id from leaking into a new Cash booking.
  useEffect(() => {
    if (location.state) {
      sessionStorage.removeItem(PENDING_GID_KEY);
      sessionStorage.removeItem(CHECKOUT_STATE_KEY);
    }
  }, []);

  // ── Persist checkout state so navigating away and back restores it ─────────
  useEffect(() => {
    if (!bookingValues) return;
    try { sessionStorage.setItem(CHECKOUT_STATE_KEY, JSON.stringify({ bookingValues, partners, partner_id, returnPath })); } catch { }
  }, [bookingValues]);

  // ── Cash payment status options - defaults to "Pending" once loaded ──
  useEffect(() => {
    (async () => {
      try {
        const res = await apicall.get("get_ezp_payment_status");
        if (res?.success && res.data?.length) {
          setPaymentStatusOptions(res.data);
          const pending = res.data.find((p) => (p.name || "").toLowerCase() === "pending");
          setCashPaymentStatus((pending || res.data[0]).id);
        }
      } catch (e) { console.log(e); }
    })();
  }, []);

  // ── Guard — after all hooks ────────────────────────────────────────────────
  useEffect(() => {
    if (!bookingValues) navigate("/vendor_bookings");
  }, []);

  if (!bookingValues) return null;

  // The server saves a booking even when its optional resource (bay, van, room
  // ...) is already in use at that time, and reports the clash in the response.
  // Surface it so staff know it was double-booked; it never blocks anything.
  const warnResourceClash = (bookingRes) => {
    if (bookingRes?.resource_conflicts) {
      toast.warn(
        `Booked, but the selected resource is also in use at this time: ${describeConflict(
          bookingRes.resource_conflicts
        )}`
      );
    }
  };

  const persistGroupId = (id) => {
    setGroupId(id);
    if (!isEditMode) sessionStorage.setItem(PENDING_GID_KEY, String(id));
  };

  // A brand-new booking (never an existing one re-checked-out via "Add
  // Item") shows the same order-confirmation popup Edit Booking already
  // shows in place, instead of landing straight on Payments - Bookings.jsx
  // already has the modal, the restore effect for this exact state shape,
  // and Close already navigates on to /payments from there.
  const finishCheckout = (bookingRes) => {
    sessionStorage.removeItem(PENDING_GID_KEY);
    sessionStorage.removeItem(CHECKOUT_STATE_KEY);
    window.dispatchEvent(new Event("setupCompletedUpdated"));
    if (!isEditMode && bookingRes) {
      navigate(returnPath, {
        state: {
          showOrderConf: true,
          confData: { ...bookingValues, booking_id: bookingRes.booking_id },
        },
      });
      return;
    }
    navigate("/payments");
  };

  // ── Cart calculations ───────────────────────────────────────────────────────
  const subtotal = cartItems.reduce((s, i) => s + i.unitPrice * i.qty, 0);
  const taxAmt = (subtotal * taxPercent) / 100;
  const tipAmt = (subtotal * tipPercent) / 100;
  const total = subtotal + taxAmt - discount + tipAmt;

  // ── Cart actions ────────────────────────────────────────────────────────────
  const changeQty = (idx, delta) => {
    setCartItems((prev) =>
      prev.map((item, i) =>
        i === idx ? { ...item, qty: Math.max(1, item.qty + delta) } : item
      )
    );
  };

  const deleteItem = (idx) => {
    setCartItems((prev) => prev.filter((_, i) => i !== idx));
  };

  // "Add Item" — reopen the booking form with current data preserved.
  // New Appointment opens inline (sidepanel, no navigation); Bookings.jsx
  // is a full page, so that flow still navigates back to it as before.
  const handleAddItem = () => {
    if (isAppointmentForm) {
      setShowAppointmentPanel(true);
      return;
    }
    navigate(returnPath, {
      state: {
        checkoutReturn: true,
        bookingValues,
        partners,
        partner_id,
      },
    });
  };

  // ── Build updated priceSection from current cart state ─────────────────────
  const buildPriceSection = () => {
    const prices = cartItems.map((item) => ({
      ...item,
      amount: item.unitPrice * item.qty,
    }));
    const newSubtotal = prices.reduce((s, i) => s + Number(i.amount), 0);
    const newTax = (newSubtotal * taxPercent) / 100;
    return [
      ...prices,
      { name: "Discount", amount: discount },
      { name: taxItem?.name || "Tax", amount: newTax.toFixed(2) },
      { name: "Total", amount: (newSubtotal + newTax - discount + tipAmt).toFixed(2) },
    ];
  };

  // ── Shared payload builder (used by both Razorpay & Stripe handlers) ─────────
  // paid_amount: use exactly what user entered — 0 if nothing entered.
  // Online payments (Razorpay/Stripe) pass paid_amount via extra to override with actual charged amount.
  const buildPayload = (extra = {}) => ({
    ...bookingValues,
    priceSection: buildPriceSection(),
    paid_amount: paidAmount,
    payment_method: selectedPayment,
    ...(groupId ? { group_id: groupId } : {}),
    ...extra,
  });

  // Persists parts the vendor staged in the New Booking form (priceSection
  // rows tagged isPart - see Bookings.jsx's addStagedPart) now that a real
  // booking row id finally exists. Uses the same bookings/:id/parts endpoint
  // PartsUsedSection already calls for Edit Booking - no new backend work.
  // A failed part must never block/roll back the booking, which by this
  // point is already created (and, for online payments, already paid for) -
  // just warn about which one(s) didn't attach so the vendor can add them
  // manually via Edit Booking.
  const flushStagedParts = async (rowId, submittedPriceSection) => {
    const staged = (submittedPriceSection || []).filter((r) => r?.isPart);
    if (!rowId || staged.length === 0) return;
    const results = await Promise.allSettled(
      staged.map((p) =>
        inventoryService.addBookingPart(rowId, {
          item_id: p.item_id,
          quantity: p.quantity,
          unit_price: p.unit_price,
        })
      )
    );
    const failed = results
      .map((r, i) => ({ r, name: staged[i].name }))
      // apicall.post resolves the parsed body even on a 4xx rather than
      // throwing - a business-rule failure looks like {success:false,...},
      // an express-validator 422 looks like {errors:[...]} with no
      // `success` key at all. Both must count as failed, or a 422 reads as "ok".
      .filter(
        ({ r }) =>
          r.status === "rejected" ||
          r.value?.success === false ||
          (Array.isArray(r.value?.errors) && r.value.errors.length > 0)
      )
      .map(({ name }) => name);
    if (failed.length > 0) {
      toast.warn(`Booking saved, but could not attach: ${failed.join(", ")}. Add manually via Edit Booking.`);
    }
  };

  // Flags the source estimation as CONVERTED once this booking actually
  // exists - fired right alongside flushStagedParts, at every point a
  // booking successfully gets created from this checkout. A failure here
  // must never block/roll back the already-created booking, same stance
  // as flushStagedParts above - just warn so the vendor can reconcile the
  // estimation manually.
  const markEstimationConverted = async (rowId) => {
    if (!bookingValues?.from_estimation || !rowId) return;
    try {
      const res = await estimationService.markConverted(bookingValues.from_estimation, rowId);
      if (!res.success) throw new Error(res.message);
    } catch (err) {
      toast.warn(`Booking saved, but the source quotation could not be marked converted: ${err.message || err}`);
    }
  };

  // Service Specifics (asset selection + complaint/action-taken note) lives
  // in its own table - Bookings.jsx couldn't save it directly for a brand
  // new booking since no id existed yet, so it carried the values through
  // on bookingValues instead. Only fires if there's actually something to
  // save, so ordinary bookings with no configured asset schema don't
  // create empty rows.
  const flushServiceSpecifics = async (rowId, customerId) => {
    if (!rowId) return;
    let { customer_asset_id, customer_complaint, action_taken, new_asset_draft } = bookingValues || {};
    if (new_asset_draft && customerId) {
      // A brand-new customer's asset couldn't be created until now - their
      // ezp_customers row (and therefore a real customer_id) only exists
      // once the booking creation above actually resolved it.
      try {
        const { label, field_values, custom_fields, asset_type_id } = new_asset_draft;
        const res = await apicall.post(
          { customer_id: customerId, asset_type_id, label, field_values, custom_fields },
          "ezp_customer_assets"
        );
        if (res?.success) customer_asset_id = res.data.id;
        else toast.warn(`Booking saved, but the details could not be saved: ${res?.errors?.[0]?.msg || res?.message || "please add them again from the customer profile."}`);
      } catch (e) { console.log(e); }
    }
    if (!customer_asset_id && !customer_complaint && !action_taken) return;
    try {
      await apicall.post({ booking_id: rowId, customer_asset_id, customer_complaint, action_taken }, "ezp_booking_service_details");
    } catch (e) { console.log(e); }
  };

  // ── Main confirm / update handler ──────────────────────────────────────────
  // Offline: create booking with payment=1 immediately.
  // Online: open payment gateway FIRST — booking created only on success.
  const handleConfirm = async () => {
    if (!selectedPayment) {
      toast.error("Please select a payment method");
      return;
    }
    if (cartItems.length === 0) {
      toast.error("Cart is empty");
      return;
    }

    setLoading(true);
    try {
      const PAYMENT_METHODS = isINR ? PAYMENT_METHODS_INR : PAYMENT_METHODS_NON_INR;
      const method = PAYMENT_METHODS.find((m) => m.id === selectedPayment);
      const selectedPartner = (partners || []).find((p) => p.id === Number(partner_id));
      const apiName =
        selectedPartner?.partner_name?.trim().toLowerCase() === "utilstop"
          ? "create_partner_booking"
          : "create_ezp_booking";
      // Bookings.jsx always wants "In Progress" (2) for a fresh booking.
      // New Appointment (clinic/doctor flow) has its own service-status
      // default (5, "Checked In") set on the form itself — respect whatever
      // it already carries instead of forcing 2 on it too.
      const defaultServiceStatus = isAppointmentForm ? (bookingValues.status ?? 9) : 2;
      const confirmedNoun = isAppointmentForm ? "Appointment" : "Booking";

      if (method?.offline) {
        // ── Offline (Cash): payment status is whatever the vendor picked
        // above (defaults to "Cash paid" since cash is usually collected
        // in person right now) - falls back to Pending (1) if the options
        // list somehow hasn't loaded yet. ──
        const payload = buildPayload({ status: defaultServiceStatus, payment: cashPaymentStatus || 1 });
        const bookingRes = await apicall.post(payload, apiName);
        const gid = Number(bookingRes?.booking_id?.split("_")?.pop?.()) || null;
        if (!gid) {
          // Surface the backend's specific reason (e.g. "Sync to Ezeitpro
          // failed...") instead of a generic message that hides why it failed.
          toast.error(bookingRes?.message || "Booking creation failed.");
          setLoading(false);
          return;
        }
        persistGroupId(gid);
        warnResourceClash(bookingRes);
        await flushStagedParts(bookingRes.ezp_row_id, payload.priceSection);
        await markEstimationConverted(bookingRes.ezp_row_id);
        await flushServiceSpecifics(bookingRes.ezp_row_id, bookingRes.customer_id);
        toast.success(isEditMode ? `${confirmedNoun} updated!` : `${confirmedNoun} confirmed!`);
        finishCheckout(bookingRes);
      } else {
        // ── Online: Razorpay (INR) or Stripe Hosted Checkout (non-INR) ──────────
        // For Stripe: we create the booking first with pending status, then redirect
        // to Stripe's hosted page. Stripe redirects back to /payments on success.
        // No Stripe.js or publishable key needed on the frontend — server-side only.

        let pendingGid = groupId;

        if (!isINR) {
          // ── Stripe: create booking as pending, then redirect to Stripe's hosted page ──
          const payload = buildPayload({ status: defaultServiceStatus, payment: 1 });
          const bookingRes = await apicall.post(payload, apiName);
          pendingGid = Number(bookingRes?.booking_id?.split("_")?.pop?.()) || null;
          if (!pendingGid) {
            toast.error(bookingRes?.message || "Booking creation failed.");
            setLoading(false);
            return;
          }
          persistGroupId(pendingGid);
          warnResourceClash(bookingRes);
          await flushStagedParts(bookingRes.ezp_row_id, payload.priceSection);
          await markEstimationConverted(bookingRes.ezp_row_id);
          await flushServiceSpecifics(bookingRes.ezp_row_id, bookingRes.customer_id);
        }

        let orderRes;
        try {
          orderRes = await apicall.post(
            {
              amount: total.toFixed(2),
              currency,
              group_id: pendingGid,
              success_return_url: window.location.origin + "/payments",
              cancel_return_url: window.location.origin + "/checkout",
            },
            "create_ezp_payment_order"
          );
        } catch (err) {
          toast.error("Payment order failed: " + (err?.message || "Unknown error"));
          setLoading(false);
          return;
        }
        if (!orderRes?.success) {
          toast.error("Could not create payment order: " + (orderRes?.message || "Unknown error"));
          setLoading(false);
          return;
        }

        if (orderRes.payment_gateway === "stripe") {
          // Save state before full-page redirect — restored if user cancels and returns
          try { sessionStorage.setItem(CHECKOUT_STATE_KEY, JSON.stringify({ bookingValues, partners, partner_id, returnPath })); } catch { }
          window.location.href = orderRes.session_url;
        } else {
          // ── Razorpay (INR) — opens their modal, booking created on success ────
          const loaded = await loadRazorpayScript();
          if (!loaded) {
            toast.error("Failed to load Razorpay SDK. Check internet connection.");
            setLoading(false);
            return;
          }

          const rzp = new window.Razorpay({
            key: orderRes.key,
            amount: orderRes.amount,
            currency: orderRes.currency,
            name: "UtilStop",
            description: "Service Booking",
            order_id: orderRes.order_id,
            prefill: {
              name: bookingValues.userName || "",
              contact: bookingValues.phoneNumber || "",
              email: bookingValues.email || "",
            },
            theme: { color: "#d01c4c" },
            handler: async (response) => {
              try {
                const payload = buildPayload({
                  status: defaultServiceStatus, // service status stays as set regardless of payment method
                  payment: 2, // "Paid Online" (ezp_payment_status) - was incorrectly 7 ("Cash paid")
                  invoice_id: response.razorpay_payment_id,
                  payment_gateway: "razorpay",
                  paid_amount: total.toFixed(2), // online: full amount was charged
                });
                const bookingRes = await apicall.post(payload, apiName);
                const gid = Number(bookingRes?.booking_id?.split("_")?.pop?.()) || null;
                if (!gid) {
                  // Payment already went through at this point — this is not a
                  // simple "try again" failure, so say so explicitly and surface
                  // the backend's specific reason instead of a generic message.
                  toast.error(
                    "Payment was successful, but booking creation failed: " +
                    (bookingRes?.message || "Unknown error") +
                    ". Please contact support with your payment reference."
                  );
                  return;
                }
                persistGroupId(gid);
                warnResourceClash(bookingRes);
                await flushStagedParts(bookingRes.ezp_row_id, payload.priceSection);
                await markEstimationConverted(bookingRes.ezp_row_id);
                await flushServiceSpecifics(bookingRes.ezp_row_id, bookingRes.customer_id);
                toast.success(`Payment successful! ${confirmedNoun} confirmed.`);
                finishCheckout(bookingRes);
              } catch (e) {
                toast.error("Payment verification failed. Contact support.");
              } finally {
                setLoading(false);
              }
            },
            modal: {
              ondismiss: () => {
                setLoading(false);
                toast.info("Payment cancelled.");
              },
            },
          });
          rzp.open();
        }
      }
    } catch (err) {
      setLoading(false);
      toast.error(err?.message || "Something went wrong");
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ background: "#f8fafc", minHeight: "100vh", padding: "24px 28px" }}>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              onClick={() => {
                if (isAppointmentForm) {
                  setShowAppointmentPanel(true);
                  return;
                }
                navigate(returnPath, { state: { checkoutReturn: true, bookingValues, partners, partner_id } });
              }}
              style={{ background: "none", border: "none", cursor: "pointer", padding: "4px 6px 4px 0", color: "#64748b", fontSize: 20, lineHeight: 1 }}
            >
              ←
            </button>
            <h2 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: "#0f172a" }}>Checkout</h2>
          </div>
          <p style={{ margin: "4px 0 0 28px", color: "#94a3b8", fontSize: 13 }}>
            New Sale · {bookingValues.date}
            {bookingValues.time ? ` · ${bookingValues.time}` : ""}
          </p>
        </div>
        <div style={{
          background: "#f1f5f9", borderRadius: 8, padding: "7px 16px",
          fontSize: 13, color: "#334155", fontWeight: 600,
          display: "flex", alignItems: "center", gap: 6,
        }}>
          👤 {bookingValues.userName || "Customer"}
        </div>
      </div>

      <div style={{ display: "flex", gap: 20, alignItems: "flex-start", flexWrap: "wrap" }}>

        {/* ══ LEFT — Cart ══════════════════════════════════════════════════════ */}
        <div style={{ flex: "1 1 520px", background: "#fff", borderRadius: 16, padding: "22px 26px", boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }}>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <span style={{ fontWeight: 700, fontSize: 17, color: "#0f172a" }}>Cart</span>
            <button
              onClick={handleAddItem}
              style={{
                background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8,
                padding: "5px 14px", fontSize: 13, color: "#334155", cursor: "pointer",
                display: "flex", alignItems: "center", gap: 5,
              }}
            >
              + Add Item
            </button>
          </div>

          {/* Items */}
          {cartItems.length === 0 && (
            <div style={{ color: "#94a3b8", fontSize: 13, textAlign: "center", padding: "32px 0" }}>
              No items in cart. Click <strong>+ Add Item</strong> to add services.
            </div>
          )}

          {cartItems.map((item, idx) => (
            <div key={idx} style={{
              display: "flex", alignItems: "center", gap: 14,
              padding: "13px 0",
              borderBottom: idx < cartItems.length - 1 ? "1px solid #f1f5f9" : "none",
            }}>
              {/* Thumbnail */}
              <div style={{
                width: 42, height: 42, borderRadius: 9, background: "#e2e8f0", flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 10, color: "#94a3b8", fontWeight: 700,
              }}>
                IMG
              </div>

              {/* Name + unit price */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontWeight: 600, fontSize: 14, color: "#0f172a",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {item.name}
                </div>
                <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 3 }}>
                  {item.isPart ? (
                    <>{item.quantity} {item.unit_of_measure} × {sym}{fmt(item.unit_price)}</>
                  ) : (
                    <>
                      {sym}{fmt(item.unitPrice)}
                      {taxPercent > 0 && ` · GST ${taxPercent}%`}
                    </>
                  )}
                </div>
              </div>

              {/* Qty controls — a part's quantity was fixed when staged in the
                  booking form (its line total already bakes it in via
                  unitPrice = the full line amount, qty pinned at 1), so the
                  generic +/- stepper here would silently double-count it;
                  show the real quantity as static text instead. */}
              {item.isPart ? (
                <div style={{ fontSize: 13, color: "#64748b", minWidth: 22, textAlign: "center", flexShrink: 0 }}>
                  {item.quantity}
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                  <button
                    onClick={() => changeQty(idx, -1)}
                    disabled={item.qty <= 1}
                    style={{
                      width: 28, height: 28, borderRadius: 6,
                      border: "1px solid #e2e8f0", background: item.qty <= 1 ? "#f8fafc" : "#fff",
                      cursor: item.qty <= 1 ? "not-allowed" : "pointer",
                      fontSize: 16, color: "#64748b",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >
                    −
                  </button>
                  <span style={{ fontWeight: 700, fontSize: 14, color: "#0f172a", width: 22, textAlign: "center" }}>
                    {item.qty}
                  </span>
                  <button
                    onClick={() => changeQty(idx, 1)}
                    style={{
                      width: 28, height: 28, borderRadius: 6,
                      border: "1px solid #e2e8f0", background: "#fff",
                      cursor: "pointer", fontSize: 18, color: "#64748b",
                      display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1,
                    }}
                  >
                    +
                  </button>
                </div>
              )}

              {/* Row total */}
              <span style={{ fontWeight: 700, fontSize: 14, color: "#0f172a", minWidth: 76, textAlign: "right" }}>
                {sym}{fmt(item.unitPrice * item.qty)}
              </span>

              {/* Delete */}
              <button
                onClick={() => deleteItem(idx)}
                title="Remove item"
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  color: "#cbd5e1", padding: "2px 4px", fontSize: 16, lineHeight: 1,
                  transition: "color 0.15s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "#ef4444")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "#cbd5e1")}
              >
                🗑
              </button>
            </div>
          ))}

          {/* Loyalty */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            background: "#fffbeb", border: "1px solid #fef3c7", borderRadius: 9,
            padding: "10px 14px", marginTop: 16,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
              <span>⭐</span>
              <span style={{ fontWeight: 600, color: "#92400e" }}>Redeem loyalty points</span>
              <span style={{ color: "#b45309" }}>0 pts available</span>
            </div>
            <div style={{ width: 18, height: 18, borderRadius: "50%", border: "2px solid #d97706" }} />
          </div>

          {/* Tip */}
          <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, color: "#64748b", fontWeight: 500 }}>Add tip:</span>
            {[{ label: "None", val: 0 }, { label: "10%", val: 10 }, { label: "15%", val: 15 }, { label: "20%", val: 20 }].map((t) => (
              <button
                key={t.val}
                onClick={() => setTipPercent(t.val)}
                style={{
                  padding: "5px 16px", border: "1.5px solid",
                  borderColor: tipPercent === t.val ? "#1d4ed8" : "#e2e8f0",
                  borderRadius: 7, fontSize: 13, cursor: "pointer",
                  background: tipPercent === t.val ? "#eff6ff" : "#fff",
                  color: tipPercent === t.val ? "#1d4ed8" : "#475569",
                  fontWeight: tipPercent === t.val ? 700 : 400,
                  transition: "all 0.15s",
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* ══ RIGHT — Summary + Payment ═════════════════════════════════════════ */}
        <div style={{ flex: "0 0 320px", display: "flex", flexDirection: "column", gap: 16 }}>

          {/* Totals */}
          <div style={{ background: "#fff", borderRadius: 16, padding: "22px 24px", boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
              <span style={{ color: "#64748b", fontSize: 14 }}>Subtotal</span>
              <span style={{ color: "#0f172a", fontSize: 14, fontWeight: 500 }}>{sym}{fmt(subtotal)}</span>
            </div>
            {taxPercent > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                <span style={{ color: "#64748b", fontSize: 14 }}>Tax (GST {taxPercent}%)</span>
                <span style={{ color: "#0f172a", fontSize: 14 }}>{sym}{fmt(taxAmt)}</span>
              </div>
            )}
            {discount > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                <span style={{ color: "#64748b", fontSize: 14 }}>Discount</span>
                <span style={{ color: "#16a34a", fontSize: 14 }}>−{sym}{fmt(discount)}</span>
              </div>
            )}
            {tipAmt > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                <span style={{ color: "#64748b", fontSize: 14 }}>Tip ({tipPercent}%)</span>
                <span style={{ color: "#0f172a", fontSize: 14 }}>{sym}{fmt(tipAmt)}</span>
              </div>
            )}
            <div style={{ borderTop: "1.5px solid #f1f5f9", paddingTop: 14, marginTop: 6 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontWeight: 700, fontSize: 17, color: "#0f172a" }}>Total</span>
                <span style={{ fontWeight: 800, fontSize: 26, color: "#0f172a" }}>
                  {sym}{fmt(total)}
                </span>
              </div>
            </div>

            {/* Paid Amount — user-editable; defaults to 0 (full total used if blank) */}
            <div style={{ borderTop: "1px dashed #e2e8f0", paddingTop: 12, marginTop: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <label style={{ color: "#64748b", fontSize: 14, fontWeight: 500 }}>Paid Amount</label>
                <div style={{ position: "relative" }}>
                  <span style={{
                    position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)",
                    color: "#94a3b8", fontSize: 13, pointerEvents: "none",
                  }}>
                    {isINR ? "₹" : "د.إ"}
                  </span>
                  <input
                    type="number"
                    min={0}
                    value={paidAmount || ""}
                    placeholder="0.00"
                    onChange={(e) => setPaidAmount(Number(e.target.value) || 0)}
                    style={{
                      width: 120, padding: "6px 8px 6px 24px",
                      border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 14,
                      color: "#0f172a", outline: "none", background: "#f8fafc",
                    }}
                  />
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ color: "#64748b", fontSize: 13 }}>Balance Due</span>
                <span style={{
                  fontSize: 13, fontWeight: 700,
                  color: total - paidAmount > 0 ? "#dc2626" : "#16a34a",
                }}>
                  {sym}{fmt(total - paidAmount)}
                </span>
              </div>
            </div>
          </div>

          {/* Payment methods */}
          <div style={{ background: "#fff", borderRadius: 16, padding: "22px 24px", boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#0f172a", marginBottom: 14 }}>
              Payment Method
            </div>

            {(() => {
              const methods = isINR ? PAYMENT_METHODS_INR : PAYMENT_METHODS_NON_INR;
              return (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  {methods.map((m) => {
                    const active = selectedPayment === m.id;
                    return (
                      <button
                        key={m.id}
                        onClick={() => {
                          if (m.id === "online") {
                            setShowOnlineDisabledModal(true);
                          } else {
                            setSelectedPayment(m.id);
                          }
                        }}
                        style={{
                          border: `1.5px solid ${active ? "#4f46e5" : "#e2e8f0"}`,
                          borderRadius: 11,
                          padding: "13px 8px",
                          background: active ? "#eef2ff" : "#fafafa",
                          cursor: "pointer",
                          textAlign: "center",
                          gridColumn: m.id === "split" ? "span 2" : "span 1",
                          transition: "all 0.15s",
                        }}
                      >
                        <div style={{ fontSize: 22, marginBottom: 5 }}>{m.emoji}</div>
                        <div style={{ fontWeight: 700, fontSize: 13, color: active ? "#4f46e5" : "#1e293b" }}>
                          {m.label}
                        </div>
                        {/* <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 3 }}>{m.sub}</div> */}
                      </button>
                    );
                  })}
                </div>
              );
            })()}

            {/* {selectedPayment && selectedPayment !== "cash" && (
              <div style={{
                marginTop: 12, padding: "8px 12px",
                background: "#eff6ff", borderRadius: 8,
                fontSize: 12, color: "#1d4ed8",
              }}>
                {isINR
                  ? "💡 Razorpay checkout will open — choose UPI, Card, Net Banking or Wallet inside it."
                  : "💡 You will be redirected to Stripe's secure hosted payment page. Come back here if you cancel."}
              </div>
            )} */}

            {selectedPayment === "cash" && paymentStatusOptions.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 7, textTransform: "uppercase", letterSpacing: 0.4 }}>
                  Payment Status
                </label>
                <select
                  value={cashPaymentStatus ?? ""}
                  onChange={(e) => setCashPaymentStatus(Number(e.target.value))}
                  style={{
                    width: "100%", padding: "9px 10px",
                    border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 14,
                    color: "#0f172a", outline: "none", background: "#f8fafc",
                  }}
                >
                  {paymentStatusOptions.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
            )}

            <button
              onClick={handleConfirm}
              disabled={loading || cartItems.length === 0}
              style={{
                width: "100%", marginTop: 18, padding: "13px",
                background: selectedPayment && !loading ? "#d01c4c" : "#c7d2fe",
                color: "#fff", border: "none", borderRadius: 10,
                fontSize: 15, fontWeight: 700,
                cursor: selectedPayment && !loading ? "pointer" : "not-allowed",
                transition: "background 0.2s",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              }}
            >
              {loading ? (
                <><Spinner animation="border" size="sm" /> Processing…</>
              ) : selectedPayment ? (
                isEditMode ? "Update" : isAppointmentForm ? "Confirm Appointment" : "Confirm Booking"
              ) : (
                "Select a payment method"
              )}
            </button>

            {/* <div style={{ textAlign: "center", marginTop: 16, color: "#94a3b8", fontSize: 11, lineHeight: 1.9 }}>
              <div>🔒 PCI-DSS secure</div>
              <div>{isINR ? "Razorpay" : "Stripe"}</div>
              <div>{isINR ? "India" : "UAE / International"}</div>
            </div> */}
          </div>
        </div>
      </div>

      {showOnlineDisabledModal && (
        <div
          style={{
            position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
            background: "rgba(15, 23, 42, 0.5)", zIndex: 9999,
            display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
          }}
        >
          <div style={{ background: "#fff", borderRadius: 16, padding: "24px 26px", maxWidth: 380, boxShadow: "0 8px 30px rgba(0,0,0,0.2)" }}>
            <div style={{ fontSize: 15, color: "#0f172a", marginBottom: 20, lineHeight: 1.5 }}>
              Contact System Admin to enable this capability
            </div>
            <button
              onClick={() => {
                setSelectedPayment("cash");
                setShowOnlineDisabledModal(false);
              }}
              style={{
                width: "100%", padding: "11px", background: "#d01c4c", color: "#fff",
                border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer",
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {isAppointmentForm && (
        <Offcanvas
          show={showAppointmentPanel}
          onHide={() => setShowAppointmentPanel(false)}
          placement="end"
          backdrop={true}
          style={{ width: "500px", minWidth: "500px", maxWidth: "500px", flexShrink: 0 }}
        >
          <Offcanvas.Header closeButton className="custom-border-bottom p-4">
            {/* <Offcanvas.Title className="page-heading">New Appointment</Offcanvas.Title> */}
          </Offcanvas.Header>
          <Offcanvas.Body className="p-0 d-flex flex-column">
            <div className="appointment-card">
              <div className="appointment-body">
                {showAppointmentPanel && (
                  <NewAppointment
                    mode="new"
                    onClose={() => setShowAppointmentPanel(false)}
                    checkoutReturnValues={bookingValues}
                  />
                )}
              </div>
            </div>
          </Offcanvas.Body>
        </Offcanvas>
      )}

    </div>
  );
}
function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}
